// src/hooks/useDeepLinkNavigation.ts
// Wraps page navigation with:
//   1. Scroll-position save for the originating page.
//   2. Deep-link state (openId, openTab) passed as module-level pending values
//      that destination pages consume on mount via consumeOpenId() / consumeOpenTab().
import { useCallback } from 'react'
import { useScrollPosition } from '../utils/ScrollPositionContext'

// Module-level pending deep-link state — survives React re-renders / page switches.
let _pendingOpenId:  string | null = null
let _pendingOpenTab: string | null = null

/** Destination pages call this on mount to pick up their target record ID. */
export function consumeOpenId(): string | null {
  const v = _pendingOpenId
  _pendingOpenId = null
  return v
}

/** Destination pages call this on mount to pick up their target tab key. */
export function consumeOpenTab(): string | null {
  const v = _pendingOpenTab
  _pendingOpenTab = null
  return v
}

export interface NavigateOptions {
  openId?:  string
  openTab?: string
}

/**
 * Returns a `navigate(targetPage, opts?)` function that:
 *  – saves the current page's scroll position before leaving
 *  – stores any openId/openTab for the destination to consume
 *  – calls the app-level onNavigate handler
 */
export function useDeepLinkNavigation(
  currentPage: string,
  onNavigate:  (page: string) => void,
) {
  const { savePosition } = useScrollPosition()

  const navigate = useCallback(
    (targetPage: string, opts?: NavigateOptions) => {
      savePosition(currentPage)
      if (opts?.openId)  _pendingOpenId  = opts.openId
      if (opts?.openTab) _pendingOpenTab = opts.openTab
      onNavigate(targetPage)
    },
    [currentPage, onNavigate, savePosition],
  )

  return { navigate }
}
